import {
  classLangTypeSchema,
  classLessonTimeSchema,
  createBaseErrorResponseSchema,
  createBaseResponseSchema,
  createLegacyBaseResponseSchema,
  curriculumTypeSchema,
  featuresSchema,
  langTypeSchema,
  lessonTimeSchema,
  rawLessonEntitySchema,
  ticketLangTypeSchema,
  trialLevelSchema,
  yesNoSchema,
} from '@server/schemas'
import { paginationOrderSchema } from '@shared/models'
import { z } from 'zod'

import { diagnosisStatusSchema, invoiceStatusSchema } from '../enums/status'

// localized object를 여기서 문자열로 접으면 화면의 effectiveLocale 정보를 잃는다.
// API 원본을 보존하고 표시 직전에 locale 우선순위를 적용한다.
const bookTitleDisplaySchema = z
  .union([z.string(), z.record(z.unknown())])
  .nullable()
  .optional()

export const skipPreStudyRequestSchema = z.object({
  classCourseId: z.number(),
})

export const skipPreStudyResponseSchema = z.object({
  resultCd: z.literal(200),
  resultCdName: z.literal('OK'),
  message: z.literal(null),
  data: z.object({
    class_id: z.number(),
    class_course_id: z.number(),
    student_id: z.number(),
    invoice_status: z.string(),
    /** @deprecated 사용하지말것 PODO_BASIC으로 나오기때문에 위험함 */
    curriculum_type: z.string(),
    is_prestudy: yesNoSchema,
  }),
})

export const selectLevelRequestSchema = z.object({
  langType: classLangTypeSchema,
  level: trialLevelSchema,
})

export const selectLevelResponseSchema = z.discriminatedUnion('resultCdName', [
  z.object({
    resultCd: z.literal(200),
    resultCdName: z.literal('OK'),
    message: z.literal(null),
    data: z.literal('success'),
  }),
  z.object({
    resultCd: z.literal(404),
    resultCdName: z.literal('DISPLAY_TRIAL_LEVEL_NOT_FOUND'),
    message: z.string(),
    data: z.literal(null),
  }),
])

export const preStudySkipCountResponseSchema = z.discriminatedUnion('resultCdName', [
  z.object({
    resultCd: z.literal(200),
    resultCdName: z.literal('OK'),
    message: z.literal(null),
    data: z.object({
      max_skip_count: z.number(),
      cur_skip_count: z.number(),
    }),
  }),
  z.object({
    resultCd: z.literal(400),
    resultCdName: z.literal('PRESTUDY_SKIP_COUNT_MAX'),
    message: z.string(),
    data: z.literal(null),
  }),
  z.object({
    resultCd: z.literal(404),
    resultCdName: z.literal('USER_NOT_FOUND'),
    message: z.string(),
    data: z.literal(null),
  }),
])

export const getNextCourseRequestSchema = z.object({
  langType: ticketLangTypeSchema,
  curriculumType: curriculumTypeSchema,
  lessonTime: classLessonTimeSchema,
  // true면 백엔드가 langType의 수강권·레벨테스트만으로 다음 코스를 정한다(다른 언어 코스 차단).
  // 미전달 시 기존 동작(언어 무관 최근 학습 기준) — 화면 언어가 확정된 호출(지정튜터 예약)만 opt-in한다.
  strictLangType: z.boolean().optional(),
})

export const getNextCourseResponseSchema = z.discriminatedUnion('resultCdName', [
  z.object({
    resultCd: z.literal(200),
    resultCdName: z.literal('OK'),
    message: z.literal(null),
    data: z
      .object({
        course_id: z.number(),
        prestudy_min: z.number(),
        level: z.number(),
        level_name: z.string(),
        level_desc: z.string(),
        level_thumbnail: z.string(),
        week: z.number(),
        week_name: z.string(),
        curriculum_type: curriculumTypeSchema,
        // 코스의 학습 언어. 백엔드 배포 전에는 없을 수 있어 optional — 화면 언어와의 불일치 검증용.
        lang_type: classLangTypeSchema.nullable().optional(),
      })
      .nullable(),
  }),
])

// getNextCourseInfo 쿼리에서 getLectureAndPrestudy를 호출한 후 반환되는 확장된 응답 스키마
export const getNextCourseInfoWithClassIdResponseSchema = z.discriminatedUnion('resultCdName', [
  z.object({
    resultCd: z.literal(200),
    resultCdName: z.literal('OK'),
    message: z.literal(null),
    data: z
      .object({
        course_id: z.number(),
        prestudy_min: z.number(),
        level: z.number(),
        level_name: z.string(),
        level_desc: z.string(),
        level_thumbnail: z.string(),
        week: z.number(),
        week_name: z.string(),
        curriculum_type: curriculumTypeSchema,
        classId: z.number(),
      })
      .nullable(),
  }),
])

export const createPodoClassRequestSchema = z.object({
  classCourseId: z.number(),
})

const podoStringResultCode = <TCode extends string>(code: TCode) =>
  z.union([z.literal(code), z.literal(Number(code))]).transform(() => code)

export const createPodoPrestudyRoomRequestSchema = z.object({
  classCourseId: z.union([z.number(), z.string()]),
  sessionId: z.string().min(1),
})

const podoEntryIdSchema = z.union([z.string(), z.number()]).transform(String)

export const createPodoPrestudyRoomSuccessResponseSchema = z.object({
  resultCd: podoStringResultCode('200'),
  result: z
    .object({
      classCourseId: z.coerce.string().optional(),
      entryTargetType: z.string().optional(),
      idType: z.string().optional(),
      id: podoEntryIdSchema.optional(),
      classId: podoEntryIdSchema.nullish(),
      class_id: podoEntryIdSchema.nullish(),
      prestudyClassId: podoEntryIdSchema.nullish(),
      prestudy_class_id: podoEntryIdSchema.nullish(),
      prestudyRoomId: z.string().min(1).optional(),
      prestudyRoomUrl: z.string().optional(),
    })
    .passthrough(),
})

export const createPodoPrestudyRoomTokenRequiredResponseSchema = z.object({
  resultCd: podoStringResultCode('409'),
  resultMsg: z.string().optional(),
  reason: z.string().optional(),
})

export const createPodoPrestudyRoomResponseSchema = z.union([
  createPodoPrestudyRoomSuccessResponseSchema,
  createPodoPrestudyRoomTokenRequiredResponseSchema,
])

export const getPodoPrestudyRoomRequestSchema = z.object({
  roomId: z.string().min(1),
})

export const getPodoPrestudyRoomSuccessResponseSchema = z.object({
  resultCd: podoStringResultCode('200'),
  result: z.object({
    classCourseId: z.coerce.string(),
    prestudyRoomId: z.string().min(1),
    prestudyRoomUrl: z.string().min(1),
    prestudyClassId: z.coerce.number().int().positive().nullable().optional(),
  }),
})

export const getPodoPrestudyRoomNotFoundResponseSchema = z.object({
  resultCd: podoStringResultCode('404'),
  resultMsg: z.string().optional(),
})

export const getPodoPrestudyRoomResponseSchema = z.union([
  getPodoPrestudyRoomSuccessResponseSchema,
  getPodoPrestudyRoomNotFoundResponseSchema,
])

export const getPodoPrestudyRoomMaterialsRequestSchema = z.object({
  classCourseId: z.union([z.number(), z.string()]),
})

export const prestudyRoomVideoItemSchema = z.object({
  id: z.coerce.string(),
  videoType: z.coerce.string().optional(),
  videoId: z.coerce.string().optional(),
  videoName: z.string(),
  videoUrl: z.string(),
  dispOrder: z.coerce.string().optional(),
  kollusMediaContentKey: z.string().optional(),
})

export const prestudyRoomAudioItemSchema = z.object({
  fileDetailId: z.coerce.string().optional(),
  attachFile: z.string(),
  attachFileName: z.string(),
  dispOrder: z.coerce.string().optional(),
})

export const getPodoPrestudyRoomMaterialsSuccessResponseSchema = z.object({
  resultCd: podoStringResultCode('200'),
  result: z.object({
    videoResult: z.array(prestudyRoomVideoItemSchema),
    audioResult: z.array(prestudyRoomAudioItemSchema),
  }),
})

export const getPodoPrestudyRoomMaterialsNotFoundResponseSchema = z.object({
  resultCd: podoStringResultCode('404'),
  resultMsg: z.string().optional(),
})

export const getPodoPrestudyRoomMaterialsResponseSchema = z.union([
  getPodoPrestudyRoomMaterialsSuccessResponseSchema,
  getPodoPrestudyRoomMaterialsNotFoundResponseSchema,
])

export const createPodoChatRequestSchema = z.object({
  classCourseId: z.number(),
})

export const createPodoChatErrorCode = z.enum(['CHAT_ALREADY_IN_PROGRESS'])

export const getLectureEnterInfoRequestSchema = z.object({
  classId: z.number(),
})

/** 실제 예약 목적지. PRE identity·방 URL·토큰과 독립된 읽기 전용 metadata. */
export const prestudyNextClassSchema = z.object({
  class_id: z.coerce.number().int().positive(),
  class_course_id: z.coerce.number().int().positive(),
  class_level: z.coerce.number().finite(),
  class_week: z.coerce.number().int().nonnegative(),
  lang_type: z.string().nullable(),
  unix_entry_available_datetime: z.number().positive(),
  unix_class_start_datetime: z.number().positive(),
  unix_class_end_datetime: z.number().positive(),
})

export const getLectureEnterInfoResponseSchema = z.discriminatedUnion('resultCd', [
  z.object({
    resultCd: z.literal('200'),
    result: z.object({
      id: z.string().nullable(),
      class_id: z.coerce.number().nullable(),
      meta_curriculum_type: z.string().nullable().optional(),
      meta_trial_context_eligible: z.boolean().nullable().optional(),
      prestudy_class_id: z.coerce.number().int().positive().nullable().optional(),
      // undefined: 이전 API. null: 지원 API에서 현재 예약 목적지 없음.
      next_class: prestudyNextClassSchema.nullable().optional(),
      zoom_id: z.string().nullable(),
      zoom_join_url: z.string().nullable(),
      student_token: z.string().nullable(),
      class_course_id: z.string(),
      class_level: z.string(),
      class_week: z.string(),
      // 실제 수업방은 유효하지만 레거시 예습행이 누락된 경우 예습 필드만 null 이다.
      pre_zoom_id: z.string().nullable(),
      pre_zoom_join_url: z.string().nullable(),
      pre_student_token: z.string().nullable(),
      unix_current: z.number(),
      /**
       * 수업 시작시각.
       *
       * 서버가 교정되기 전에는 "가장 임박한 수업" 기준이고 보낼 수업방이 없으면 2999-12-31
       * 자리표시자가 담겼다. 교정 후에는 요청한 classId 기준이 되고, 스케줄이 없는 행
       * (예습방처럼 시작=종료인 경우)에서는 **null 이 온다** — 그래서 nullable 이다.
       *
       * 시각 계산에 쓰지 마라. 예습방 이동은 unix_next_class_start_datetime,
       * 수업 종료는 unix_class_end_datetime 을 쓴다.
       */
      unix_class_start_datetime: z.number().nullable().optional(),
      /** 예습방에서 수업방으로 보낼 시각. 보낼 곳이 없으면 없거나 null 이다. */
      unix_next_class_start_datetime: z.number().nullable().optional(),
      /** 요청한 classId 의 종료시각. 특정할 수 없으면 없거나 null 이다. */
      unix_class_end_datetime: z.number().nullable().optional(),
      lesson_time: z.number().nullable().optional(),
      timer_target_id: z.number().nullable().optional(),
    }),
  }),
  z.object({
    resultCd: z.literal('404'),
    result: z.string(),
  }),
])

export const updatePreStudyTimeRequestSchema = z.object({
  classId: z.union([z.number(), z.string()]),
  randomUserKey: z.string(),
  studentId: z.number(),
})

export const updatePreStudyTimeSuccessResponseSchema = z.discriminatedUnion('resultCd', [
  z.object({
    resultCd: z.literal(200),
  }),
])

export const updatePreStudyTimeErrorResponseSchema = z.discriminatedUnion('resultCd', [
  z.object({
    resultCd: z.literal(400),
    resultMsg: z.string(),
    result: z.null(),
  }),
  z.object({
    resultCd: z.literal(403),
    resultMsg: z.string(),
    result: z.null(),
  }),
])

export const getLectureRoomInfoRequestSchema = z.object({
  classId: z.number(),
})

export const videoItemSchema = z.object({
  video_url: z.string(),
  video_name: z.string(),
})

export const audioItemSchema = z.object({
  attach_file: z.string(),
  attach_file_name: z.string(),
})

export const getLectureRoomInfoResponseSchema = z.discriminatedUnion('resultCd', [
  z.object({
    resultCd: z.literal('200'),
    result: z.object({
      videoResult: z.array(videoItemSchema),
      audioResult: z.array(audioItemSchema),
    }),
  }),
  z.object({
    resultCd: z.literal('404'),
    result: z.string(),
  }),
])

export const getReplayEnterInfoRequestSchema = z.object({
  classId: z.number(),
})

export const getReplayEnterInfoResponseSchema = z.discriminatedUnion('resultCd', [
  z.object({
    resultCd: z.literal('200'),
    result: z.object({
      service_type: z.string(),
      zoom_id: z.string(),
      student_token: z.string(),
    }),
  }),
  z.object({
    resultCd: z.literal('404'),
    resultMsg: z.string(),
  }),
])

export const getLectureReplayVideoRequestSchema = z.object({
  classId: z.number(),
})

/** 다시보기 mp4 생성 상태. 상태 정의는 레몬보드가 갖고 백엔드는 그대로 전달한다. */
export const lectureReplayVideoStatusSchema = z.enum([
  'NONE',
  'QUEUED',
  'RECORDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
])

export const getLectureReplayVideoResponseSchema = z.discriminatedUnion('resultCd', [
  z.object({
    resultCd: z.literal('200'),
    result: z.object({
      status: lectureReplayVideoStatusSchema,
      // 다운로드용 presigned URL. COMPLETED 에서만 내려온다(1시간 유효).
      url: z.string().nullish(),
      // 예상 생성 소요(분). 백엔드가 수업 길이로 계산한다 — 생성중 툴팁 표시용.
      estimatedMinutes: z.number().nullish(),
    }),
  }),
  z.object({
    resultCd: z.literal('404'),
    resultMsg: z.string().optional(),
  }),
])

export type LectureReplayVideoStatus = z.infer<typeof lectureReplayVideoStatusSchema>

export const isLectureReplayVideoPending = (status: LectureReplayVideoStatus | null | undefined) =>
  status === 'QUEUED' || status === 'RECORDING' || status === 'PROCESSING'

export const requestFinishPodoClassRequestSchema = z.object({
  studentId: z.number(),
  classId: z.union([z.number(), z.string()]),
  randomUserKey: z.string(),
})

export const requestFinishPodoClassResponseSchema = z.discriminatedUnion('resultCd', [
  z.object({
    resultCd: z.literal(200),
  }),
])

export const scoreNameTypeSchema = z.enum(['FLUENCY', 'GRAMMAR', 'VOCAB', 'COMPLEXITY'])

// 레슨 리포트 응답 스키마
const scoreSchema = z.discriminatedUnion('name', [
  z.object({
    name: scoreNameTypeSchema.extract([scoreNameTypeSchema.Enum.FLUENCY]),
    score_user: z.number(),
    score_average: z.number(),
  }),
  z.object({
    name: scoreNameTypeSchema.extract([scoreNameTypeSchema.Enum.VOCAB]),
    score_user: z.number(),
    score_average: z.number(),
  }),
  z.object({
    name: scoreNameTypeSchema.extract([scoreNameTypeSchema.Enum.COMPLEXITY]),
    score_user: z.number(),
    score_average: z.number(),
  }),
  /**
   * @deprecated 당분간 사용하지않을 예정
   */
  z.object({
    name: scoreNameTypeSchema.extract([scoreNameTypeSchema.Enum.GRAMMAR]),
    score_user: z.number(),
    score_average: z.number(),
  }),
])

const wordFeedbackSchema = z.object({
  id: z.string(),
  type: z.literal('WORD'),
  phrase: z.object({
    vocab: z.string(),
    vocab_translation: z.string(),
    vocab_diction_speaker_lang: z.string().nullable().optional(),
    vocab_diction_native_lang: z.string().nullable().optional(),
    example: z.string(),
    example_translation: z.string(),
    example_diction_speaker_lang: z.string().nullable().optional(),
    example_diction_native_lang: z.string().nullable().optional(),
  }),
})

export type WordFeedback = z.infer<typeof wordFeedbackSchema>

const correctionFeedbackSchema = z.object({
  id: z.string(),
  type: z.literal('CORRECTION'),
  phrase: z.object({
    ai_explanation: z.string(),
    ai_sentence: z.array(z.string()),
    ai_highlight: z.array(z.number()),
    ai_sentence_diction_speaker_lang: z.string().nullable().optional(),
    ai_sentence_diction_native_lang: z.string().nullable().optional(),
    student_sentence: z.array(z.string()),
    student_highlight: z.array(z.number()),
    student_sentence_diction_speaker_lang: z.string().nullable().optional(),
    student_sentence_diction_native_lang: z.string().nullable().optional(),
  }),
})

export type CorrectionFeedback = z.infer<typeof correctionFeedbackSchema>

const feedbackSchema = z.discriminatedUnion('type', [wordFeedbackSchema, correctionFeedbackSchema])

export type Feedback = z.infer<typeof feedbackSchema>

export const generateDiagnosisReportRequestSchema = z.object({
  classId: z.number(),
})

export const diagnosisReportStatusSchema = z.enum(['REQUESTED', 'PROCESSING', 'COMPLETED', 'FAILED'])

export const generateDiagnosisReportResponseSchema = z.object({
  resultCd: z.literal(200),
  resultCdName: z.literal('OK'),
  message: z.literal(null),
  data: diagnosisReportStatusSchema,
})

export const getLessonDiagnosisReportRequestSchema = z.object({
  classId: z.number(),
})

export type GetLessonDiagnosisReportRequest = z.infer<typeof getLessonDiagnosisReportRequestSchema>

export const lessonDiagnosisReportResponseSchema = z.object({
  resultCd: z.literal(200),
  resultCdName: z.literal('OK'),
  message: z.literal(null),
  data: z.object({
    lecture_id: z.number(),
    lang_type: classLangTypeSchema,
    level_name: z.string(),
    week_name: z.string(),
    utc_lesson_at: z.string(),
    podo_level: z.string(),
    scores: z.array(scoreSchema),
    total_score_grade: z.enum(['A', 'B', 'C', 'D', 'E', 'F']).optional().default('A'),
    feedbacks: z.array(feedbackSchema),
  }),
})

export type LessonDiagnosisReportResponse = z.infer<typeof lessonDiagnosisReportResponseSchema>

export const questionTypeSchema = z.enum([
  'MULTI_CHOICE_WORD',
  'BLANK_FILLING',
  'MULTI_CHOICE_SENTENCE',
  'SENTENCE_MAKING',
])

// 질문 스키마들
const baseQuestionSchema = z.object({
  question: z.string(),
})

// BLANK_FILLING 질문 스키마
const blankFillingQuestionSchema = z.object({
  question_id: z.string(),
  content_level: z.number(),
  lang_type: classLangTypeSchema,
  type: questionTypeSchema.extract([questionTypeSchema.Enum.BLANK_FILLING]),
  source: baseQuestionSchema.extend({
    body: z.array(z.string()),
    answer: z.array(z.string()),
    hint: z.array(z.string()),
    ai_translation: z.string(),
    ai_explanation: z.string(),
  }),
})

// SENTENCE_MAKING 질문 스키마
const sentenceMakingQuestionSchema = z.object({
  question_id: z.string(),
  content_level: z.number(),
  lang_type: classLangTypeSchema,
  type: questionTypeSchema.extract([questionTypeSchema.Enum.SENTENCE_MAKING]),
  source: baseQuestionSchema.extend({
    answer: z.string(),
    body: z.object({
      original_sentence: z.string(),
      vocab: z.string(),
      vocab_translation: z.string(),
    }),
    hint: z.object({
      highlight: z.string(),
      sentence: z.string(),
      sentence_translation: z.string(),
    }),
  }),
})

// MULTI_CHOICE_WORD 질문 스키마
const multiChoiceWordQuestionSchema = z.object({
  question_id: z.string(),
  content_level: z.number(),
  lang_type: classLangTypeSchema,
  type: questionTypeSchema.extract([questionTypeSchema.Enum.MULTI_CHOICE_WORD]),
  source: baseQuestionSchema.extend({
    body: z.object({
      options: z.array(z.string()),
      options_foreign: z.array(z.string()),
      vocab: z.string(),
      vocab_diction_speaker_lang: z.string().nullable().optional(),
      vocab_diction_native_lang: z.string().nullable().optional(),
    }),
    ai_explanation: z.string(),
    answer: z.number(),
    hint: z.object({
      highlight: z.string(),
      sentence: z.string(),
      sentence_translation: z.string(),
      sentence_diction_native_lang: z.string().nullable().optional(),
      sentence_diction_speaker_lang: z.string().nullable().optional(),
    }),
  }),
})

// MULTI_CHOICE_SENTENCE 질문 스키마
const multiChoiceSentenceQuestionSchema = z.object({
  question_id: z.string(),
  content_level: z.number(),
  lang_type: classLangTypeSchema,
  type: questionTypeSchema.extract([questionTypeSchema.Enum.MULTI_CHOICE_SENTENCE]),
  source: baseQuestionSchema.extend({
    body: z.object({
      options: z.array(z.string()),
      options_foreign: z.array(z.string()),
      sentence: z.string(),
      sentence_diction_native_lang: z.string().nullable().optional(),
      sentence_diction_speaker_lang: z.string().nullable().optional(),
    }),
    ai_explanation: z.string(),
    answer: z.number(),
    hint: z.array(
      z.object({
        vocab: z.string(),
        vocab_diction_speaker_lang: z.string().nullable().optional(),
        vocab_diction_native_lang: z.string().nullable().optional(),
        vocab_translation: z.string(),
      }),
    ),
  }),
})

// 모든 질문 타입을 포함하는 discriminated union
export const questionSchema = z.discriminatedUnion('type', [
  multiChoiceSentenceQuestionSchema,
  multiChoiceWordQuestionSchema,
  blankFillingQuestionSchema,
  sentenceMakingQuestionSchema,
])

export const getQuestionsRequestSchema = z.object({
  classId: z.number(),
  feedbackIds: z.array(z.string()),
})

// 질문 배열 스키마
export const getQuestionsResponseSchema = z.object({
  resultCd: z.literal(200),
  resultCdName: z.literal('OK'),
  message: z.literal(null),
  data: z.array(questionSchema),
})

export type GetQuestionsRequest = z.infer<typeof getQuestionsRequestSchema>
export type Question = z.infer<typeof questionSchema>
export type MultiChoiceWordQuestion = z.infer<typeof multiChoiceWordQuestionSchema>
export type BlankFillingQuestion = z.infer<typeof blankFillingQuestionSchema>
export type MultiChoiceSentenceQuestion = z.infer<typeof multiChoiceSentenceQuestionSchema>
export type SentenceMakingQuestion = z.infer<typeof sentenceMakingQuestionSchema>
export type GetQuestionsResponse = z.infer<typeof getQuestionsResponseSchema>

export const checkAnswerRequestSchema = z.object({
  user_answer: z.string(),
  master_answer: z.string(),
  question_id: z.string(),
})

// checkAnswer API 응답 스키마
export const checkAnswerSuccessResponseSchema = z.object({
  correct: z.literal(true),
  user_answer: z.array(z.string()),
  user_highlight: z.array(z.number()),
  master_answer: z.array(z.string()),
  master_highlight: z.array(z.number()),
  ai_explanation: z.string(),
  question_type: questionTypeSchema,
})

export const checkAnswerFailureResponseSchema = z.object({
  correct: z.literal(false),
  user_answer: z.array(z.string()),
  user_highlight: z.array(z.number()),
  master_answer: z.array(z.string()),
  master_highlight: z.array(z.number()),
  ai_explanation: z.string().nullable().optional(),
  question_type: questionTypeSchema,
})

export const checkAnswerResponseSchema = z.object({
  resultCd: z.literal(200),
  resultCdName: z.literal('OK'),
  message: z.literal(null),
  data: z.discriminatedUnion('correct', [checkAnswerSuccessResponseSchema, checkAnswerFailureResponseSchema]),
})

export type CheckAnswerResponse = z.infer<typeof checkAnswerResponseSchema>

// 타입 내보내기
export type CheckAnswerSuccessResponse = z.infer<typeof checkAnswerSuccessResponseSchema>
export type CheckAnswerFailureResponse = z.infer<typeof checkAnswerFailureResponseSchema>
export type CheckAnswerRequest = z.infer<typeof checkAnswerRequestSchema>

export const changePronunciationRequestSchema = z.object({
  input: z.string(),
  input_type: z.enum(['kanji', 'hiragana']),
  output_type: z.enum(['hiragana', 'korean']),
})

export const changePronunciationResponseSchema = z.object({
  resultCd: z.literal(200),
  resultCdName: z.literal('OK'),
  message: z.literal(null),
  data: z.object({
    output: z.string(),
  }),
})

export type ChangePronunciationRequest = z.infer<typeof changePronunciationRequestSchema>
export type ChangePronunciationResponse = z.infer<typeof changePronunciationResponseSchema>

export const nextLectureSchema = z.object({
  class_id: z.number(),
  class_status: z.string(),
  course_id: z.number(),
  curriculum_type: curriculumTypeSchema,
  invoice_status: invoiceStatusSchema,
  lesson_time: classLessonTimeSchema,
  lang_type: classLangTypeSchema.optional(),
  level: z.number(),
  level_desc: z.string(),
  level_name: z.string(),
  level_thumbnail: z.string(),
  book_title: bookTitleDisplaySchema,
  book_name: z.string().nullable().optional(),
  class_level_name: z.string().nullable().optional(),
  tutor_id: z.number(),
  utc_class_datetime: z.string().datetime(),
  week: z.number(),
  week_name: z.string(),
  diagnosis_status: diagnosisStatusSchema,
  reservable: z.boolean(),
  platform_type: z.enum(['LEMONBOARD', 'PAGECALL']),
  class_id_for_booking: z.number().nullable(),
  features: featuresSchema.array().optional().default([]),
  teacher_name: z.string().nullable().optional(),
  my_rating: z.number().nullable().optional(),
  rating_submitted: z.boolean().optional().default(false),
  // 리뷰(NPS) 작성 가능 여부 = 백엔드가 시행일·작성 기한·처리 이력을 반영해 계산. 미수신 시 undefined → 프론트에서 기존 24시간으로 폴백.
  can_write_review: z.boolean().optional(),
  // 튜터지정권으로 예약된 레슨 여부 (백엔드: tutor-connect ledger USE 조인). 미수신 시 false.
  tutor_connect: z.boolean().optional().default(false),
})

export const getLectureListRequestSchema = z.object({
  page: z.number(),
  limit: z.number(),
  order: paginationOrderSchema,
  invoice_status: z.array(invoiceStatusSchema),
})

export const getLectureListResponseDataSchema = z.object({
  total: z.number(),
  lectures: z.array(nextLectureSchema),
})

export const getLectureListResponseSchema = z.discriminatedUnion('resultCd', [
  z.object({
    resultCd: z.literal(200),
    resultCdName: z.string(),
    message: z.literal(null),
    data: getLectureListResponseDataSchema,
  }),
])

export const convertRubyRequestSchema = z.object({
  text: z.string(),
  highlight: z.array(z.number()).optional(),
})

export const convertRubyResponseSchema = z.object({
  result: z.array(z.object({ text: z.string(), furigana: z.string().optional(), isHighlighted: z.boolean() })),
})

export const getCompletedLectureRequestSchema = z.object({
  classId: z.number(),
})

export const getCompletedLectureResponseSchema = z.object({
  resultCd: z.literal(200),
  resultCdName: z.literal('OK'),
  message: z.literal(null),
  data: z.object({
    class_id: z.number(),
    course_id: z.number(),
    tutor_id: z.number(),
    level: z.number(),
    level_name: z.string(),
    level_desc: z.string(),
    level_thumbnail: z.string().nullable(),
    book_title: bookTitleDisplaySchema,
    book_name: z.string().nullable().optional(),
    class_level_name: z.string().nullable().optional(),
    week: z.number(),
    week_name: z.string(),
    curriculum_type: curriculumTypeSchema,
    lesson_time: classLessonTimeSchema,
    lang_type: classLangTypeSchema,
    utc_class_datetime: z.string(),
    replay_url: z.string().nullable(),
    drawing_url: z.string().nullable(),
  }),
})

export const copyPodoClassRoomRequestSchema = z.object({
  classId: z.number(),
})

export const copyPodoClassRoomResponseSchema = z.object({
  resultCd: z.literal(200),
  resultCdName: z.literal('OK'),
  message: z.literal(null),
  data: z.object({
    url: z.string().url(),
  }),
})

export const getCharacterChatRecommendLessonRequestSchema = z.object({
  langType: langTypeSchema,
})

export const getCharacterChatRecommendLessonResponseSchema = createBaseResponseSchema(
  z.array(
    z.object({
      classCourseId: z.number(),
      name: z.string(),
      description: z.string(),
      thumbnail: z.string(),
      lessonTime: lessonTimeSchema,
      classWeek: z.number(),
    }),
  ),
)

export const characterChatLevelSchema = z.object({
  level: z.number(),
  progress: z.number(),
  displayProgress: z.number(),
  total: z.number(),
})

export const getCharacterChatLevelResponseSchema = createBaseResponseSchema(characterChatLevelSchema)

export const pronunciationLessonSchema = rawLessonEntitySchema.extend({
  book_name: z.string().nullable(),
  class_type: z.string().nullable(),
  class_level: z.string().nullable(),
  class_level_name: z.string().nullable(),
  class_course_id: z
    .string()
    .nullable()
    .transform((val) => (val ? Number(val) : null)),
  class_week: z.string().nullable(),
  class_id: z.string().transform(Number),
  student_user_id: z.string().nullable().optional(),
})

export const getPronunciationListResponseSchema = z.array(pronunciationLessonSchema)

export const getNextLectureInfoResponseSchema = createLegacyBaseResponseSchema(
  rawLessonEntitySchema.extend({
    teacher_id: z.string().transform(Number),
    class_id: z.string().transform(Number),
    student_user_id: z.string().nullable().optional(),
    class_level: z.string().default('0').transform(Number),
    class_week: z.string().default('0').transform(Number),
    class_course_id: z.string().transform(Number),
    // 튜터지정권 예약 여부. 스키마에 없으면 zod 가 응답에서 제거해 홈 카드의 지정 레슨 판별이 항상 false 가 된다.
    tutor_connect: z.boolean().optional().default(false),
  }),
)

export const getNextLectureInfoErrorResponseSchema = z.object({
  resultCd: z.string().transform(Number),
  resultMsg: z.string(),
})

export const getTutorLessonsBySelectedTopicResponseSchema = createBaseResponseSchema(
  z
    .object({
      classId: z.number(),
      prestudyClassId: z.number(),
      classCourseId: z.number(),
      userId: z.number(),
      teacherId: z.number(),
      classStartTime: z.string().datetime(),
      classEndTime: z.string().datetime(),
      langType: langTypeSchema,
      bookTitle: bookTitleDisplaySchema.transform((value) => value ?? ''),
      description: z.string(),
      level: z.number(),
      week: z.number(),
    })
    .nullable(),
)

export const getTutorLessonTopicsRequestSchema = z.object({
  langType: langTypeSchema,
})

export const getTutorLessonTopicsResponseSchema = createBaseResponseSchema(
  z.array(
    z.object({
      classCourseId: z.number(),
      name: z.string(),
      description: z.string(),
      thumbnail: z.string().url(),
      lessonTime: lessonTimeSchema,
      classWeek: z.number(),
    }),
  ),
)

export const getTutorLessonTopicsErrorResponseSchema = createBaseErrorResponseSchema(400, 'INVALID_AI_LEVEL_UP')

export const getAiCharacterDetailInfoRequestSchema = z.object({
  classCourseId: z.number(),
})

export const getAiCharacterDetailInfoResponseSchema = createBaseResponseSchema(
  z.object({
    classCourseId: z.number(),
    chatName: z.string(),
    description: z.string(),
    tags: z.array(z.string()),
    missions: z.array(z.string()),
    thumbnail: z.string().url(),
    characters: z.array(z.object({ name: z.string(), description: z.string(), thumbnail: z.string().url() })),
  }),
)

export const getNextLectureListResponseSchema = createBaseResponseSchema(
  z.array(
    z.object({
      id: z.string(),
      classId: z.number(),
      title: z.string(),
      instructor: z.string(),
      scheduledAt: z.string(),
      duration: z.number(),
      level: z.string(),
      status: z.enum(['confirmed', 'pending', 'cancelled', 'recommended']),
      canCancel: z.boolean(),
      joinUrl: z.string().optional(),
    }),
  ),
)

export const getUserBookingActionResponseSchema = z.array(
  z.object({
    id: z.string(),
    classId: z.number(),
    title: z.string(),
    instructor: z.string(),
    scheduledAt: z.string(),
    duration: z.number(),
    level: z.string(),
    status: z.enum(['confirmed', 'pending', 'cancelled', 'recommended']),
    canCancel: z.boolean(),
    joinUrl: z.string().optional(),
  }),
)

export const levelUpSkipResponseSchema = createBaseResponseSchema(characterChatLevelSchema)

export const getLectureAndPrestudyRequestSchema = z.object({
  classCourseId: z.number(),
})

export const getLectureAndPrestudySuccessResponseSchema = createBaseResponseSchema(
  z.object({
    lecture_id: z.number(),
    prestudy_id: z.number().nullable().optional(),
  }),
)

export const getLectureAndPrestudyPrestudyNotFoundResponseSchema = createBaseErrorResponseSchema(
  404,
  'PRESTUDY_NOT_FOUND',
)

export const getLectureAndPrestudyLectureNotFoundResponseSchema = createBaseErrorResponseSchema(
  404,
  'LECTURE_NOT_FOUND',
)

export const getLectureAndPrestudyNotFoundResponseSchema = z.union([
  getLectureAndPrestudyPrestudyNotFoundResponseSchema,
  getLectureAndPrestudyLectureNotFoundResponseSchema,
])

export const getLectureAndPrestudyResponseSchema = z.union([
  getLectureAndPrestudySuccessResponseSchema,
  getLectureAndPrestudyNotFoundResponseSchema,
])

// ============================================
// 예약 관련 스키마 (Booking Schedules)
// ============================================

// 날짜 목록 조회 스키마
export const getWeekDatesResponseSchema = z.object({
  resultCd: z.string(),
  result: z.array(
    z.object({
      available_date: z.string(),
    }),
  ),
})

// 튜터 스케줄 조회 스키마
export const getTutorSchedulesForRegRequestSchema = z.object({
  classId: z.number(),
  startDateTime: z.string(),
  endDateTime: z.string(),
})

export const getTutorSchedulesForRegResponseSchema = createBaseResponseSchema(
  z.array(
    z.object({
      schedule_time: z.string(),
      cnt: z.number(),
    }),
  ),
)

// 레슨 정보 조회 스키마 (예약용)
export const getBookingLectureInfoRequestSchema = z.object({
  classId: z.number(),
})

export const getBookingLectureInfoResponseSchema = createBaseResponseSchema(
  z.object({
    lesson_time: classLessonTimeSchema,
    book_name: z.string().nullable(),
    class_date: z.string().nullable(),
    class_start_time: z.string().nullable(),
    trial_yn: z.string().nullable(),
    curriculum_type: z.string().nullable().optional(),
    paid_trial: z.boolean().optional(),
    lang_type: classLangTypeSchema.optional(),
    ticket_expire_date: z.string().nullable(),
    ticket_start_datetime: z.string().nullish(),
    ticket_expire_datetime: z.string().nullish(),
    ticket_availability_periods: z
      .array(z.object({ start_datetime: z.string(), expire_datetime: z.string() }))
      .optional(),
    ticket_issued_periods: z
      .array(
        z.object({
          start_datetime: z.string(),
          expire_datetime: z.string(),
          ticket_id: z.string(),
          subscribe_mapp_id: z.string().nullish(),
        }),
      )
      .optional(),
    available_table: z.record(z.boolean()).nullable().optional(),
    unlimit_yn: z.string().nullable().optional(),
    active_unlimited_reservation_conflict: z.boolean().optional().default(false),
  }),
)

export const getActiveUnlimitedReservationConflictRequestSchema = z.object({
  classCourseId: z.number(),
})

export const getActiveUnlimitedReservationConflictResponseSchema = createBaseResponseSchema(
  z.object({
    active_unlimited_reservation_conflict: z.boolean(),
  }),
)

// 페널티 목록 조회 스키마
export const getPenaltyListRequestSchema = z.object({
  classId: z.number(),
})

export const getPenaltyListResponseSchema = z.object({
  resultCd: z.string(),
  result: z
    .array(
      z.object({
        start_datetime: z.string(),
        end_datetime: z.string(),
        city: z.string().nullable().optional(),
        payment_lang_type: z.string().nullable().optional(),
      }),
    )
    .nullable(),
})

// 홀딩 목록 조회 스키마
export const getHoldingListRequestSchema = z.object({
  classId: z.number(),
})

export const getHoldingListResponseSchema = z.object({
  resultCd: z.string(),
  result: z.array(
    z.object({
      start_datetime: z.string(),
      end_datetime: z.string(),
    }),
  ),
})

// 예약 요청 스키마
export const bookScheduleRequestSchema = z.object({
  classId: z.number(),
  classDatetime: z.string(),
  lessonTime: classLessonTimeSchema,
})

export const bookScheduleResponseSchema = createBaseResponseSchema(z.unknown().optional())

// 예약 변경 요청 스키마
export const changeScheduleRequestSchema = z.object({
  classId: z.number(),
  classDatetime: z.string(),
  lessonTime: classLessonTimeSchema,
})

export const changeScheduleResponseSchema = createBaseResponseSchema(z.unknown().optional())

// 타입 내보내기
export type GetWeekDatesResponse = z.infer<typeof getWeekDatesResponseSchema>
export type GetTutorSchedulesForRegRequest = z.infer<typeof getTutorSchedulesForRegRequestSchema>
export type GetTutorSchedulesForRegResponse = z.infer<typeof getTutorSchedulesForRegResponseSchema>
export type GetBookingLectureInfoRequest = z.infer<typeof getBookingLectureInfoRequestSchema>
export type GetBookingLectureInfoResponse = z.infer<typeof getBookingLectureInfoResponseSchema>
export type GetPenaltyListRequest = z.infer<typeof getPenaltyListRequestSchema>
export type GetPenaltyListResponse = z.infer<typeof getPenaltyListResponseSchema>
export type GetHoldingListRequest = z.infer<typeof getHoldingListRequestSchema>
export type GetHoldingListResponse = z.infer<typeof getHoldingListResponseSchema>
export type BookScheduleRequest = z.infer<typeof bookScheduleRequestSchema>
export type BookScheduleResponse = z.infer<typeof bookScheduleResponseSchema>
export type ChangeScheduleRequest = z.infer<typeof changeScheduleRequestSchema>
export type ChangeScheduleResponse = z.infer<typeof changeScheduleResponseSchema>
